import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { WintSuperkollAPIModelWintSalaryDeviations } from "wint-api-client/dist/types";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { WintSalaryEnums } from "models/enums";
import { SavedSalaryDepositDeviationTypeId } from "utils/constants/salaryConstants";
import * as Yup from "yup";

const WithdrawalClassifications: WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeClassification[] = [
  WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeClassification.SavedSalaryAndVacationPayWithdrawal,
  WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeClassification.SavedSalaryWithdrawal,
  WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeClassification.SalaryVacationPayWithdrawal,
];

const SalaryDeviationHelper = {
  /**
   * Checks if the given deviation is a mileage type
   * @param deviation - The deviation AutocompleteListItem to check
   * @returns true if the deviation is mileage, false otherwise
   */
  isMileageDeviation: (deviation?: AutocompleteListItem): boolean => {
    return (
      deviation?.data?.informationType ===
        WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationInformationType.DecimalValue &&
      deviation?.data?.informationTypeUnit ===
        WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationInformationTypeUnit.Kilometer
    );
  },

  /**
   * Checks if the given deviation is a travel compensation type
   * @param deviation - The deviation AutocompleteListItem to check
   * @returns true if the deviation is travel compensation, false otherwise
   */
  isTravelCompensation: (deviation?: AutocompleteListItem): boolean => {
    return (
      (deviation?.data?.informationType ===
        WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationInformationType.TravelCompensationDomestic ||
        deviation?.data?.informationType ===
          WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationInformationType.TravelCompensationInternational) &&
      deviation?.data?.category === WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeCategory.Compensations
    );
  },

  /**
   * Checks if the given deviation is an international travel compensation type
   * @param deviation - The deviation AutocompleteListItem to check
   * @returns true if the deviation is international travel compensation, false otherwise
   */
  isInternationalTravelCompensation: (deviation?: AutocompleteListItem): boolean => {
    return (
      SalaryDeviationHelper.isTravelCompensation(deviation) &&
      deviation?.data?.informationType ===
        WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationInformationType.TravelCompensationInternational
    );
  },

  /**
   * Fuel benefit type. Admin only offers FuelBenefit under Compensations.
   */
  isFuelBenefit: (deviation?: AutocompleteListItem): boolean => {
    return (
      deviation?.data?.informationType ===
        WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationInformationType.FuelBenefit &&
      deviation?.data?.category === WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeCategory.Compensations
    );
  },

  /**
   * Commission type: one engine-written deviation per deal, carrying exactly one amount + description
   * entry. Edit-only — the salary engine creates it, a user never does.
   */
  isCommission: (deviation?: AutocompleteListItem): boolean => {
    return (
      deviation?.data?.informationType ===
        WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationInformationType.Commission &&
      deviation?.data?.category === WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeCategory.Compensations
    );
  },

  /**
   * Number input or undefined. Formik gives "" for a cleared number field; junk input must not become NaN.
   */
  toOptionalNumber: (value: unknown): number | undefined => {
    if (value === undefined || value === null || value === "") return undefined;
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
  },

  /**
   * Creates a Yup date validation that requires the field when travel compensation is selected
   * @param fieldName - The name of the field for the test (e.g., "fromDate-required", "toDate-required")
   * @returns A Yup test function
   */
  createTravelCompensationDateRequiredTest: (fieldName: string) => {
    return function (this: Yup.TestContext, value: Date | null | undefined): boolean {
      const root = (this.from?.[this.from.length - 1]?.value || (this.options?.context as any)?.root) as
        | SalaryDeviationFormTypes.WintDeviationCreate
        | undefined;
      const deviation = root?.deviation;
      if (SalaryDeviationHelper.isTravelCompensation(deviation)) {
        return value !== null && value !== undefined;
      }
      return true;
    };
  },

  /**
   * Creates a Yup string validation that requires the field when travel compensation is selected
   * @param fieldName - The name of the field for the test (e.g., "fromTime-required", "toTime-required")
   * @returns A Yup test function
   */
  createTravelCompensationTimeRequiredTest: (fieldName: string) => {
    return function (this: Yup.TestContext, value: string | null | undefined): boolean {
      const root = (this.from?.[this.from.length - 1]?.value || (this.options?.context as any)?.root) as
        | SalaryDeviationFormTypes.WintDeviationCreate
        | undefined;
      const deviation = root?.deviation;
      if (SalaryDeviationHelper.isTravelCompensation(deviation)) {
        return value !== null && value !== undefined && value !== "";
      }
      return true;
    };
  },

  /**
   * Creates a Yup validation that requires the AutocompleteListItem field when international travel compensation is selected
   * @param fieldName - The name of the field for the test (e.g., "countryCode-required")
   * @returns A Yup test function
   */
  createTravelCompensationAutocompleteRequiredTest: (fieldName: string) => {
    return function (this: Yup.TestContext, value: AutocompleteListItem | null | undefined): boolean {
      const root = (this.from?.[this.from.length - 1]?.value || (this.options?.context as any)?.root) as
        | SalaryDeviationFormTypes.WintDeviationCreate
        | undefined;
      const deviation = root?.deviation;
      if (SalaryDeviationHelper.isInternationalTravelCompensation(deviation)) {
        return value !== null && value !== undefined;
      }
      return true;
    };
  },

  /**
   * Gets the root form values from Yup test context
   * @param context - Yup test context
   * @returns The root form values or undefined
   */
  getRootFormValues: (context: Yup.TestContext): SalaryDeviationFormTypes.WintDeviationCreate | undefined => {
    return (context.from?.[context.from.length - 1]?.value || (context.options?.context as any)?.root) as
      | SalaryDeviationFormTypes.WintDeviationCreate
      | undefined;
  },

  /**
   * Creates a Yup date validation that requires the field when NOT travel compensation
   * @param fieldName - The name of the field for the test (e.g., "yearMonth-required")
   * @returns A Yup test function
   */
  createNonTravelCompensationDateRequiredTest: (fieldName: string) => {
    return function (this: Yup.TestContext, value: Date | null | undefined): boolean {
      const root = SalaryDeviationHelper.getRootFormValues(this);
      const deviation = root?.deviation;
      // Only required when NOT travel compensation
      if (!SalaryDeviationHelper.isTravelCompensation(deviation)) {
        return value !== null && value !== undefined;
      }
      return true;
    };
  },

  /**
   * Creates a Yup array validation that requires the field when NOT travel compensation
   * @param fieldName - The name of the field for the test (e.g., "sumDescriptions-required")
   * @returns A Yup test function
   */
  createNonTravelCompensationArrayRequiredTest: (fieldName: string) => {
    return function (this: Yup.TestContext, value: any[] | null | undefined): boolean {
      const root = SalaryDeviationHelper.getRootFormValues(this);
      const deviation = root?.deviation;
      // Only required when NOT travel compensation
      if (!SalaryDeviationHelper.isTravelCompensation(deviation)) {
        return value !== null && value !== undefined && Array.isArray(value) && value.length > 0;
      }
      return true;
    };
  },

  /**
   * Creates a Yup validation that requires positive sum values when mileage deviation is selected
   * @param fieldName - The name of the field for the test (e.g., "sum-positive-for-mileage")
   * @returns A Yup test function
   */
  createMileagePositiveSumTest: (fieldName: string) => {
    return function (this: Yup.TestContext, value: number | null | undefined): boolean {
      const root = SalaryDeviationHelper.getRootFormValues(this);
      const deviation = root?.deviation;
      // Only validate positive when mileage deviation
      if (SalaryDeviationHelper.isMileageDeviation(deviation)) {
        return value !== null && value !== undefined && value > 0;
      }
      return true;
    };
  },

  isHoursDeviation: (deviation?: AutocompleteListItem): boolean => {
    return (
      deviation?.data?.informationType ===
        WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationInformationType.DecimalValue &&
      deviation?.data?.informationTypeUnit ===
        WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationInformationTypeUnit.Hours
    );
  },

  /**
   * Checks if the given deviation has DecimalValue informationType
   * @param deviation - The deviation AutocompleteListItem to check
   * @returns true if the deviation is DecimalValue type, false otherwise
   */
  isDecimalValueDeviation: (deviation?: AutocompleteListItem): boolean => {
    return (
      deviation?.data?.informationType ===
      WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationInformationType.DecimalValue
    );
  },

  /**
   * Determines if the Amount section should be shown based on deviation type and information type
   * @param deviationType - The selected deviation type
   * @param deviation - The deviation AutocompleteListItem to check
   * @returns true if the Amount section should be shown, false otherwise
   */
  shouldShowAmountSection: (
    deviationType?: WintSalaryEnums.SalaryDeviationType,
    deviation?: AutocompleteListItem
  ): boolean => {
    if (!deviationType || !deviation) return false;
    // Withdrawals and the deposit are Compensations + DecimalValue too, but they take a single amount —
    // withdrawalSection renders them instead of the repeatable amount+description table.
    if (SalaryDeviationHelper.takesSingleAmount(deviation.data)) return false;

    const isDecimalValue = SalaryDeviationHelper.isDecimalValueDeviation(deviation);
    // Commission reuses the amount+description table, limited to its single row by amountSection.
    const isCommission = SalaryDeviationHelper.isCommission(deviation);

    return (
      (deviationType === WintSalaryEnums.SalaryDeviationType.WorkingHours && isDecimalValue) ||
      (deviationType === WintSalaryEnums.SalaryDeviationType.Compensation && (isDecimalValue || isCommission))
    );
  },

  /**
   * Determines if the Time Deviation section should be shown
   * @param deviationType - The selected deviation type
   * @param deviation - The deviation AutocompleteListItem to check (required for WorkingHours)
   * @returns true if the Time Deviation section should be shown, false otherwise
   */
  shouldShowTimeDeviationSection: (
    deviationType?: WintSalaryEnums.SalaryDeviationType,
    deviation?: AutocompleteListItem
  ): boolean => {
    // Absence always uses time deviation section
    if (deviationType === WintSalaryEnums.SalaryDeviationType.Absence) {
      return true;
    }

    // WorkingHours with non-DecimalValue informationType uses time deviation section as fallback
    if (
      deviationType === WintSalaryEnums.SalaryDeviationType.WorkingHours &&
      deviation !== undefined &&
      !SalaryDeviationHelper.isDecimalValueDeviation(deviation)
    ) {
      return true;
    }

    return false;
  },

  /**
   * Determines if the comment ("Övrig information") field should be shown.
   *
   * Mirrors the server rule in SalaryDeviationValidationService, which accepts a comment only for
   * the Compensations category and otherwise refuses the whole submit with
   * CommentNotAllowedForNonCompensationDeviation. Gated on the category the API reports for the
   * deviation, not on which input section happens to be rendered — the previous condition
   * (!showTimeDeviation) also matched WorkingHours with a DecimalValue information type and
   * TimeBalances, so the form offered a field those submits could never carry (DEV-28130).
   *
   * @param deviation - The deviation AutocompleteListItem to check
   * @returns true if the comment field should be shown, false otherwise
   */
  shouldShowCommentSection: (deviation?: AutocompleteListItem): boolean => {
    // Commission: the entry's description is the editable label and is saved as the comment too,
    // since the comment is what the payslip row reads.
    return (
      deviation?.data?.category ===
        WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeCategory.Compensations &&
      !SalaryDeviationHelper.isCommission(deviation)
    );
  },

  /**
   * Determines if the Travel Compensation section should be shown
   * @param deviation - The deviation AutocompleteListItem to check
   * @returns true if the Travel Compensation section should be shown, false otherwise
   */
  shouldShowTravelCompensationSection: (deviation?: AutocompleteListItem): boolean => {
    return SalaryDeviationHelper.isTravelCompensation(deviation);
  },

  /**
   * Checks if the given deviation type is Absence
   * Used for conditional validation and API request generation
   * @param deviationType - The deviation type to check
   * @returns true if the deviation type is Absence, false otherwise
   */
  isAbsenceDeviation: (deviationType?: WintSalaryEnums.SalaryDeviationType): boolean => {
    return deviationType === WintSalaryEnums.SalaryDeviationType.Absence;
  },

  /**
   * Checks if the given deviation has ParentalLeave classification
   * @param deviation - The deviation AutocompleteListItem to check
   * @returns true if the deviation has ParentalLeave classification, false otherwise
   */
  isParentalLeaveClassification(deviation?: AutocompleteListItem): boolean {
    if (!deviation?.data) return false;
    return (
      deviation.data.classification ===
      WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeClassification.ParentalLeave
    );
  },

  /**
   * Checks if the given deviation has Vacation classification.
   * Vacation deviations are always reported as full days - no hours/minutes input.
   * @param deviation - The deviation AutocompleteListItem to check
   * @returns true if the deviation has Vacation classification, false otherwise
   */
  isVacationClassification(deviation?: AutocompleteListItem): boolean {
    if (!deviation?.data) return false;
    return (
      deviation.data.classification ===
      WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeClassification.Vacation
    );
  },

  /**
   * Determines whether the current-month placement opt-in may be offered for this deviation.
   *
   * Only `Optional` is a choice: `Never` forbids it and `Always` already places every deviation of
   * the type on its own month, so a control there would offer a decision the user does not have.
   * Read from the type's own `currentMonthPlacement` — never from a classification or a type-id
   * list, since the eligible types are prod data flipped by hand (DEV-28594) and no live type
   * carries `isSickLeave`.
   *
   * @param deviation - The deviation AutocompleteListItem to check
   * @returns true if the opt-in control may be rendered, false otherwise
   */
  allowsCurrentMonthPlacementChoice(deviation?: AutocompleteListItem): boolean {
    if (!deviation?.data) return false;
    return (
      deviation.data.currentMonthPlacement === WintSuperkollAPIModelWintSalaryDeviations.CurrentMonthPlacement.Optional
    );
  },

  /**
   * Determines if the Time Balances (comp-time / Komp) section should be shown.
   *
   * Gated on both the TimeBalances category and the CompTime classification, so only
   * comp-time deviations render this section.
   *
   * @param deviationType - The selected deviation type
   * @param deviation - The deviation AutocompleteListItem to check
   * @returns true if the Time Balances section should be shown, false otherwise
   */
  shouldShowTimeBalancesSection: (
    deviationType?: WintSalaryEnums.SalaryDeviationType,
    deviation?: AutocompleteListItem
  ): boolean => {
    if (!deviationType || !deviation) return false;
    return (
      deviationType === WintSalaryEnums.SalaryDeviationType.TimeBalances &&
      deviation.data?.classification ===
        WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeClassification.CompTime
    );
  },

  /**
   * True for the three saved-salary / vacation-pay withdrawal classifications.
   * Takes the raw classification so both the form (which holds an AutocompleteListItem) and the
   * API response mapper (which holds the deviation type) can ask the same question.
   */
  isWithdrawalClassification: (
    classification?: WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeClassification
  ): boolean => classification !== undefined && WithdrawalClassifications.includes(classification),

  /**
   * The withdrawal classification of a selected deviation, or undefined when it is not a
   * withdrawal. The value decides which pot balances the section renders.
   */
  getWithdrawalClassification: (
    deviation?: AutocompleteListItem
  ): WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeClassification | undefined => {
    const classification = deviation?.data?.classification;
    return SalaryDeviationHelper.isWithdrawalClassification(classification) ? classification : undefined;
  },

  /**
   * True for the types entered as one amount on the current month: the three withdrawal
   * classifications and the saved-salary deposit ("Insättning sparad lön"). Takes the raw type so
   * both the form and the API response mapper ask the same question.
   */
  takesSingleAmount: (type?: WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationType): boolean =>
    SalaryDeviationHelper.isWithdrawalClassification(type?.classification) ||
    type?.id?.toLowerCase() === SavedSalaryDepositDeviationTypeId,

  /**
   * Determines if the withdrawal section should be shown. The withdrawal types and the deposit are
   * seeded as Compensations + DecimalValue, so this is the amount section's condition narrowed to
   * the three withdrawal classifications and the deposit type.
   */
  shouldShowWithdrawalSection: (
    deviationType?: WintSalaryEnums.SalaryDeviationType,
    deviation?: AutocompleteListItem
  ): boolean => {
    if (!deviationType || !deviation) return false;
    return (
      deviationType === WintSalaryEnums.SalaryDeviationType.Compensation &&
      SalaryDeviationHelper.isDecimalValueDeviation(deviation) &&
      SalaryDeviationHelper.takesSingleAmount(deviation.data)
    );
  },
};

export default SalaryDeviationHelper;
